import {
  ASSISTANT_CONTEXT_ENTITY_KINDS,
  ASSISTANT_ENTITY_CONTEXT_ID_MAX_CHARACTERS,
  ASSISTANT_ENTITY_CONTEXT_LABEL_MAX_CHARACTERS,
  type AssistantContextEntityKind,
  type AssistantEntityContext,
} from '@prompt-bridge/shared/ai-assistant-core';

import {EMPTY_PLACEHOLDER} from 'shared/web';

interface AssistantEntityReferenceInput {
  id: string;
  label?: string | null;
}

type AssistantRelatedEntityIds = Partial<
  Record<AssistantContextEntityKind, string | AssistantEntityReferenceInput | null | undefined>
>;

/**
 * Builds the entity-context slice from the raw related-record ids a page has loaded, keeping the
 * wire-contract bounds in ONE place instead of in every page mapper.
 *
 * An id the contract would reject is dropped rather than published: dropping it loses one
 * cross-entity route suggestion, whereas sending it would fail validation for the whole assistant
 * request. Returns `undefined` when no related entity survives, so the slice is absent rather than
 * an empty object.
 */
export const buildAssistantEntityContext = (
  ids: AssistantRelatedEntityIds
): AssistantEntityContext | undefined => {
  const entities = ASSISTANT_CONTEXT_ENTITY_KINDS.reduce<AssistantEntityContext>((result, kind) => {
    const input = ids[kind];
    const id = typeof input === 'string' ? input : input?.id;

    if (
      typeof id !== 'string' ||
      id.length === 0 ||
      id.length > ASSISTANT_ENTITY_CONTEXT_ID_MAX_CHARACTERS
    ) {
      return result;
    }

    const label = typeof input === 'object' ? input?.label : undefined;
    const reference =
      typeof label === 'string' && label.length > 0 && label !== EMPTY_PLACEHOLDER
        ? {id, label: label.slice(0, ASSISTANT_ENTITY_CONTEXT_LABEL_MAX_CHARACTERS)}
        : {id};

    return {...result, [kind]: reference};
  }, {});

  return Object.keys(entities).length > 0 ? entities : undefined;
};
